import React, { useState, useEffect } from 'react'
import api from '../api/client'
import type { Event, Shift } from '../types'
import { X, Copy } from 'lucide-react'
import { addDays } from 'date-fns'
import { utcToLocalDateAndTime, localDateAndTimeToUtc } from '../utils/timezone'
import { useLocale } from '../contexts/LocaleContext'

const WINTER_EVENT_SKILLS = [
  'School schaatsbegeleiding',
  'Kassa / schaatsverhuur',
  'Toezichthouder',
  'Passe-partouts controle',
  'Op- afbouw',
  'Technische ondersteuning',
  'BHV',
  'Social media content creatie',
  'Fotograaf',
  'Video producent'
]

interface DuplicateShiftModalProps {
  isOpen: boolean
  onClose: () => void
  shift: Shift | null
  onShiftDuplicated: () => void
}

const DuplicateShiftModal: React.FC<DuplicateShiftModalProps> = ({
  isOpen,
  onClose,
  shift,
  onShiftDuplicated
}) => {
  const { t } = useLocale()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  const [formData, setFormData] = useState({
    event_id: '',
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
    max_volunteers: 1,
    shift_type: 'public',
    required_skills: [] as string[]
  })

  useEffect(() => {
    if (isOpen && shift) {
      fetchEvents()
      // Pre-fill form with shift data
      const originalStartTime = new Date(shift.start_time)
      const originalEndTime = new Date(shift.end_time)
      
      // Default to next day, but user can change
      const newStartTime = addDays(originalStartTime, 1)
      const newEndTime = addDays(originalEndTime, 1)
      
      const startDateTime = utcToLocalDateAndTime(newStartTime.toISOString())
      const endDateTime = utcToLocalDateAndTime(newEndTime.toISOString())
      
      setFormData({
        event_id: shift.event_id || '',
        title: `${shift.title} (Copy)`,
        description: shift.description || '',
        start_date: startDateTime.date,
        start_time: startDateTime.time,
        end_date: endDateTime.date,
        end_time: endDateTime.time,
        location: shift.location,
        max_volunteers: shift.max_volunteers,
        shift_type: shift.shift_type || 'public',
        required_skills: shift.required_skills || []
      })
    }
  }, [isOpen, shift])

  const fetchEvents = async () => {
    try {
      const data = await api.events.list()
      setEvents(data || [])
    } catch (err: any) {
      console.error('Error fetching events:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await api.shifts.create({
        event_id: formData.event_id || undefined,
        title: formData.title,
        description: formData.description || undefined,
        start_time: localDateAndTimeToUtc(formData.start_date, formData.start_time),
        end_time: localDateAndTimeToUtc(formData.end_date, formData.end_time),
        location: formData.location,
        max_volunteers: formData.max_volunteers,
        shift_type: formData.shift_type,
        required_skills: formData.required_skills.length > 0 ? formData.required_skills : undefined
      })

      onShiftDuplicated()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate shift')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'max_volunteers' ? parseInt(value) || 1 : value
    }))
  }

  const handleSkillToggle = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      required_skills: prev.required_skills.includes(skill)
        ? prev.required_skills.filter(s => s !== skill)
        : [...prev.required_skills, skill]
    }))
  }

  if (!isOpen || !shift) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Copy className="h-5 w-5 mr-2" />
                  {t('duplicateShift.title')}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              <div className="space-y-4">
                {/* Event Selection */}
                <div>
                  <label htmlFor="event_id" className="block text-sm font-medium text-gray-700">
                    {t('duplicateShift.event')}
                  </label>
                  <select
                    id="event_id"
                    name="event_id"
                    value={formData.event_id}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('duplicateShift.noEvent')}</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    {t('duplicateShift.shiftTitle')} *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t('duplicateShift.description')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Start Date and Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                      {t('duplicateShift.startDate')} * (dd/mm/yyyy)
                    </label>
                    <input
                      type="text"
                      id="start_date"
                      name="start_date"
                      required
                      value={formData.start_date}
                      onChange={handleInputChange}
                      placeholder="dd/mm/yyyy"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
                      {t('duplicateShift.startTime')} * (HH:mm)
                    </label>
                    <input
                      type="time"
                      id="start_time"
                      name="start_time"
                      required
                      value={formData.start_time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* End Date and Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                      {t('duplicateShift.endDate')} * (dd/mm/yyyy)
                    </label>
                    <input
                      type="text"
                      id="end_date"
                      name="end_date"
                      required
                      value={formData.end_date}
                      onChange={handleInputChange}
                      placeholder="dd/mm/yyyy"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
                      {t('duplicateShift.endTime')} * (HH:mm)
                    </label>
                    <input
                      type="time"
                      id="end_time"
                      name="end_time"
                      required
                      value={formData.end_time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    {t('duplicateShift.location')} *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Max Volunteers */}
                <div>
                  <label htmlFor="max_volunteers" className="block text-sm font-medium text-gray-700">
                    {t('duplicateShift.maxVolunteers')} *
                  </label>
                  <input
                    type="number"
                    id="max_volunteers"
                    name="max_volunteers"
                    required
                    min="1"
                    value={formData.max_volunteers}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Shift Type */}
                <div>
                  <label htmlFor="shift_type" className="block text-sm font-medium text-gray-700">
                    {t('createShift.shiftType')} *
                  </label>
                  <select
                    id="shift_type"
                    name="shift_type"
                    value={formData.shift_type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="public">{t('createShift.public')}</option>
                    <option value="private">{t('createShift.private')}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.shift_type === 'private' ? t('createShift.privateDescription') : t('createShift.publicDescription')}
                  </p>
                </div>

                {/* Required Skills */}
                <div className="border-t pt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('createShift.requiredSkills')} ({t('createShift.optional')})
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('createShift.requiredSkillsDescription')}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {WINTER_EVENT_SKILLS.map(skill => (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => handleSkillToggle(skill)}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          formData.required_skills.includes(skill)
                            ? 'bg-blue-100 border-blue-300 text-blue-800'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {skill}
                      </button>
                    ))}
                  </div>
                  {formData.required_skills.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      {t('createShift.selectedSkills')}: {formData.required_skills.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('duplicateShift.duplicating')}
                  </div>
                ) : (
                  t('duplicateShift.duplicate')
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                {t('duplicateShift.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default DuplicateShiftModal
